import Vue from 'vue'
import Vuex from 'vuex'
import { API } from 'aws-amplify'
import { amplifyProxy, directApi } from '@/services/directApi'
// modules
import DashboardCards from './modules/DashboardCards'
import LandlordsCount from './modules/Landlords/LandlordsCount'
import LandlordsRecords from './modules/Landlords/LandlordsRecords'
import TenantsCount from './modules/Tenants/TenantsCount'
import TenantsRecords from './modules/Tenants/TenantsRecords'
import PropertiesCount from './modules/Properties/PropertiesCount'
import PropertiesRecords from './modules/Properties/PropertiesRecords'
import ContractorsCount from './modules/Contractors/ContractorsCount'
import ContractorsRecords from './modules/Contractors/ContractorsRecords'
import ContractorsManagement from './modules/Contractors/ContractorsManagement'
import OutgoingPayments from './modules/OutgoingPayments'
import UnreconciledPayments from './modules/UnreconciledPayments'
import PaidInvoices from './modules/Reports/PaidInvoices'
import UnpaidInvoices from './modules/Reports/UnpaidInvoices'
import DepositsHeld from './modules/Reports/DepositsHeld'
import OpenBankingRequests from './modules/OpenBanking/OpenBankingRequests'
import InvoiceRecords from './modules/Invoices/InvoiceRecords'
import PaymentRequests from './modules/PaymentRequests'
import RStB from './modules/Reports/RStB'
import CurrencyData from './modules/Currency/CurrencyData'
import ManagementFeeForecast from './modules/Forecast/ManagementFeeForecast'
import TenanciesManagement from './modules/Tenancies/TenanciesManagement'
import {authAgentOSSignIn, authAgentOSSignOut} from './agentosAuth'

Vue.use(Vuex)

export default new Vuex.Store({
  modules: {
    DashboardCards,
    LandlordsCount,
    LandlordsRecords,
    TenantsCount,
    TenantsRecords,
    PropertiesCount,
    PropertiesRecords,
    OutgoingPayments,
    UnreconciledPayments,
    PaidInvoices,
    DepositsHeld,
    ContractorsCount,
    ContractorsRecords,
    ContractorsManagement,
    OpenBankingRequests,
    InvoiceRecords,
    PaymentRequests,
    UnpaidInvoices,
    RStB,
    CurrencyData,
    ManagementFeeForecast,
    TenanciesManagement
  },
  state: {
    config: {
      headers: {},
      apiName: 'agentPayRestApi'
    },
    userSpecific: 0,
    user: null,
    activeUser: '',
    branchID: null,
    branchName: 'Company Wide',
    multiBranchAccess: false,
    allBranches: [],
    errorMessages: [],
    alternativeDashboard: false,
    lightTheme: true,
    salesChargeTypes: '',
    nominalCodes: ''
  },
  mutations: {
    SET_ERROR(state, data) {
      state.errorMessages.push(data)
    },
    CLEAR_ERRORS(state) {
      state.errorMessages = []
    },
    SET_USER_DATA(state, userData) {
      state.user = userData
      state.user["loginTime"] = Date.now()
      localStorage.setItem('user', JSON.stringify(userData))
      state.config.headers = {
        token: userData.LoginToken
      }
    },
    EXPIRE_LOGIN(state){
      const then = new Date(state.user.loginTime)
      const now = new Date()
      const msBetweenDates = Math.abs(then.getTime() - now.getTime())
      const hoursBetweenDates = msBetweenDates / (60 * 60 * 1000)
      if (hoursBetweenDates > 23) {
        state.user = null
        localStorage.setItem('user', null)
      }
    },
    CLEAR_USER_DATA(state) {
      // Reset core state
      state.userSpecific = ''
      state.user = null
      state.activeUser = ''
      state.branchID = null
      state.branchName = 'Company Wide'
      state.multiBranchAccess = false
      state.allBranches = []
      state.errorMessages = []
      state.config.headers = {}

      // Reset all module states
      // Dashboard
      state.DashboardCards.outGoingPayments = ''
      state.DashboardCards.unreconciledPayments = ''
      state.DashboardCards.insights = ''
      state.DashboardCards.income = ''
      state.DashboardCards.portfolioGrowth = ''
      state.LandlordsCount.landlords = ''
      state.TenantsCount.tenants = ''
      state.PropertiesCount.properties = ''
      state.ContractorsCount.contractors = ''

      //Unreconciled & reconciled records
      state.UnreconciledPayments.unreconciledPaymentsRecords = ''
      state.UnreconciledPayments.reconciledPaymentsRecords = ''
     
      // Outgoing payments
      state.OutgoingPayments.landlordsToPay = ''
      state.OutgoingPayments.contractorsToPay = ''
      state.OutgoingPayments.tenantsToPay = ''
      
      //Property records
      state.PropertiesRecords.propertiesManagedRecords = ''
      state.PropertiesRecords.propertiesWithoutOwnerRecords = ''
      state.PropertiesRecords.propertiesRentCollectedRecords = ''
      state.PropertiesRecords.propertiesWithoutTenantsRecords = ''
      state.PropertiesRecords.propertiesNotManagedRecords = ''
      state.PropertiesRecords.propertiesNewTenanciesRecords = ''
      
      //Tenants records
      state.TenantsRecords.tenantsActiveRecords = ''
      state.TenantsRecords.tenantsArrearsRecords = ''
      state.TenantsRecords.tenantsNotChasedRecords = ''

      //Landlord records
      state.LandlordsRecords.landlordsEmaillessRecords = ''
      state.LandlordsRecords.landlordsOwingRecords = ''
      state.LandlordsRecords.landlordsTenPercentBalanceDifference = ''
      state.LandlordsRecords.landlordsBanklessRecords = ''
      state.LandlordsRecords.allLandlordRecords = ''

      //Contractor records
      state.ContractorsRecords.contractorsAll = ''
      state.ContractorsRecords.bankless = ''
      state.ContractorsRecords.emailless = ''
      state.ContractorsRecords.onHold = ''

      // Reports
      state.PaidInvoices.paidInvoices = ''
      state.DepositsHeld.accounts = ''
      state.DepositsHeld.records = ''

      //Open banking
      state.OpenBankingRequests.bankAccounts = ''

      state.salesChargeTypes = ''
      state.nominalCodes = ''

      localStorage.removeItem('user')
      localStorage.removeItem('currency')
      authAgentOSSignOut()
    },
    SET_SINGLE_BRANCH_USER(state, data) {
      state.activeUser = data.ActiveUser
      state.branchID = data.ManagingBranchOID
    },
    SET_MULTI_BRANCH_USER(state, data) {
      state.activeUser = data.ActiveUser
      state.multiBranchAccess = data.MultiBranchAccess
    },
    SET_SELECTED_BRANCH(state, data) {
      state.branchID = data.OID
      state.branchName = data.Name
    },
    SET_ALL_BRANCHES(state, data) {
      state.allBranches = data
    },
    SET_ALTERNATIVE_DASHBOARD(state, data) {
      state.alternativeDashboard = data
    },
    SET_LIGHT_THEME(state, data) {
      state.lightTheme = data
    },
    SET_SALES_CHARGE_TYPES(state, data) {
      state.salesChargeTypes = data
    },
    SET_NOMINAL_CODES(state, data) {
      state.nominalCodes = data
    }
  },
  actions: {
    // Dashboard Data Fetching
    async fetchDashboardData({ dispatch }) {
      try {
        await Promise.all([
          dispatch('DashboardCards/getUnreconciledPayments'),
          dispatch('DashboardCards/getOutGoingPayments'),
          dispatch('DashboardCards/getInsights'),
          dispatch('DashboardCards/getIncome'),
          dispatch('DashboardCards/getPortfolioGrowth'),
          dispatch('PropertiesCount/getPropertiesCount'),
          dispatch('LandlordsCount/getLandlordsCount'),
          dispatch('TenantsCount/getTenantsCount'),
          dispatch('ContractorsCount/getContractorsCount')
        ])
      } catch (error) {
        // Errors are handled silently
      }
    },

    // Outgoing Payments Data Fetching
    async fetchOutgoingPaymentsData({ dispatch }) {
      try {
        await Promise.all([
          dispatch('OutgoingPayments/getLandlordsPayments'),
          dispatch('OutgoingPayments/getTenantsPayments'),
          dispatch('OutgoingPayments/getContractorsPayments'),
          dispatch('OutgoingPayments/getZeroBalancePayments')
        ])
      } catch (error) {
        // Errors are handled silently
      }
    },

    async fetchUnreconciledsPaymentsData({ dispatch }) {
      try {
        await Promise.all([
          dispatch('UnreconciledPayments/getUnreconciledPaymentsRecords'),
          dispatch('UnreconciledPayments/getReconciledPaymentsRecords')
        ])
      } catch (error) {
        // Errors are handled silently
      }
    },

    async fetchTenantsRecords({ dispatch }) {
      try {
        await Promise.all([
          dispatch('TenantsRecords/getTenantsActiveRecords'),
          dispatch('TenantsRecords/getTenantsArrearsRecords'),
          dispatch('TenantsRecords/getTenantsNotChasedRecords'),
          dispatch('TenantsRecords/getTenantsHistoricRecords')
        ])
      } catch (error) {
        // Errors are handled silently
      }
    },

    async fetchPropertyRecords({ dispatch }) {
      try {
        await Promise.all([
          dispatch('PropertiesRecords/getPropertiesManagedRecords'),
          dispatch('PropertiesRecords/getPropertiesOwnerlessRecords'),
          dispatch('PropertiesRecords/getPropertiesRentCollectedRecords'),
          dispatch('PropertiesRecords/getPropertiesWithoutTenantsRecords'),
          dispatch('PropertiesRecords/getPropertiesNotManagedRecords'),
          dispatch('PropertiesRecords/getPropertiesNewTenanciesRecords'),
          dispatch('PropertiesRecords/getPropertiesHistoricTenanciesRecords')
        ])
      } catch (error) {
        // Errors are handled silently
      }
    },

    async fetchLandlordRecords({ dispatch }) {
      try {
        await Promise.all([
          dispatch('LandlordsRecords/getAllLandlordsRecords'),
          dispatch('LandlordsRecords/getLandlordsBanklessRecords'),
          dispatch('LandlordsRecords/getLandlordsEmaillessRecords'),
          dispatch('LandlordsRecords/getLandlordsOwingRecords'),
          dispatch('LandlordsRecords/getLandlordsTenPercentDifferenceRecords')
        ])
      } catch (error) {
        // Errors are handled silently
      }
    },

    async fetchContractorRecords({ dispatch }) {
      try {
        await Promise.all([
          dispatch('ContractorsRecords/getAllContractorsRecords'),
          dispatch('ContractorsRecords/getContractorsBanklessRecords'),
          dispatch('ContractorsRecords/getContractorsEmaillessRecords'),
          dispatch('ContractorsRecords/getContractorsOnHold')
        ])
      } catch (error) {
        // Errors are handled silently
      }
    },

    async fetchOpenBankingData({ dispatch }) {
      try {
        await dispatch('OpenBankingRequests/getBankAccounts')
      } catch (error) {
        // Errors are handled silently
      }
    },
    // Authentication Actions
    async login({ commit, dispatch }, credentials) {
      try {
        const response = await API.post('agentPayRestApi', '/auth/login', {
          body: {
            Username: credentials.username,
            Password: credentials.password
          }
        })
        
        if (response.data) {
          // First authenticate with AWS Cognito
          await authAgentOSSignIn(
            response.data.UserOID, 
            response.data.UserDisplayName, 
            response.data.UserToken
          )
          
          // Then set user data with headers
          commit('SET_USER_DATA', response.data)
          
          // Finally make the follow-up API calls
          await Promise.all([
            dispatch('sessionStatus'),
            dispatch('CurrencyData/getCurrencyData')
          ])
        }
      } catch (error) {
        throw error
      }
    },

    async loginAgentOSSessionID({ commit, dispatch }, credentials) {
      try {
        const response = await API.post('agentPayRestApi', '/auth/loginFromOS', {
          body: {
            ShortName: credentials.shortName,
            SessionID: credentials.sessionID
          }
        })
        
        if (response.data) {
          // First authenticate with AWS Cognito
          await authAgentOSSignIn(
            response.data.UserOID, 
            response.data.UserDisplayName, 
            response.data.UserToken
          )
          
          // Then set user data with headers
          commit('SET_USER_DATA', response.data)
          
          // Finally make the follow-up API calls
          await Promise.all([
            dispatch('sessionStatus'),
            dispatch('CurrencyData/getCurrencyData')
          ])
        }
      } catch (error) {
        throw error
      }
    },

    async sessionStatus({ state, commit, dispatch }) {
      try {
        const response = await amplifyProxy.get(`/${state.user.ClientName}/session/status`, {
          headers: state.config.headers
        })

        if (response.data) {
          if (response.data.MultiBranchAccess) {
            commit('SET_MULTI_BRANCH_USER', response.data)
            await dispatch('getAllBranches')
          } else {
            commit('SET_SINGLE_BRANCH_USER', response.data)
          }
        }
      } catch (error) {
        await dispatch('logout')
      }
    },

    async getAllBranches({ state, commit }) {
      try {
        const response = await amplifyProxy.get(`/${state.user.ClientName}/company/branches/0/1000`, {
          headers: state.config.headers
        })

        
        // The response should have a data property with the branches
        if (response && response.data) {
          
          // Check if data is an array of branches
          let branchesArray = []
          if (Array.isArray(response.data)) {
            branchesArray = response.data
          } else if (response.data.Data && Array.isArray(response.data.Data)) {
            // Handle paginated response format
            branchesArray = response.data.Data
          }
          
          if (branchesArray.length > 0) {
            const alphabeticallySorted = branchesArray.sort((a, b) => {
              const nameA = a.Name.toLowerCase()
              const nameB = b.Name.toLowerCase()
              if (nameA < nameB) return -1
              if (nameA > nameB) return 1
              return 0
            })
            
            alphabeticallySorted.unshift({
              Name: '*** Company Wide',
              OID: null
            })
            
            commit('SET_ALL_BRANCHES', alphabeticallySorted)
          } else {
            commit('SET_ALL_BRANCHES', [{
              Name: '*** Company Wide',
              OID: null
            }])
          }
        } else {
          commit('SET_ALL_BRANCHES', [{
            Name: '*** Company Wide',
            OID: null
          }])
        }
      } catch (error) {
        throw error
      }
    },

    setSelectedBranch({ commit }, branchObj) {
      commit('SET_SELECTED_BRANCH', branchObj)
    },

    async logout({ state, commit }) {
      try {
        await amplifyProxy.del(`/${state.user.ClientName}/session/logout`, {
          headers: state.config.headers
        })
        commit('CLEAR_USER_DATA')
      } catch (error) {
        // Still clear user data even if the API call fails
        commit('CLEAR_USER_DATA')
      }
    },

    // Message Controller
    async sendMessage({ state }, payload) {
      try {
        return await directApi.sendComment(
          state.user.ClientName,
          payload.attachments,
          {
            recordID: payload.recordID,
            source: payload.source,
            comment: payload.comment,
            commentRecipient: 'AgentPay'
          },
          state.user.LoginToken
        )
      } catch (error) {
        throw error
      }
    },

    async getSpecificPersonById({ state }, personID) {
      try {
        return await amplifyProxy.get(`/${state.user.ClientName}/company/person/${personID}`, {
          headers: state.config.headers
        })
      } catch (error) {
        throw error
      }
    },

    async getSalesChargeTypes({ state, commit }) {
      try {
        const response = await amplifyProxy.get(`/${state.user.ClientName}/company/salestypes`, {
          headers: state.config.headers
        })

        if (response.data?.Data) {
          commit('SET_SALES_CHARGE_TYPES', response.data.Data)
        }
      } catch (error) {
        throw error
      }
    },

    async getNominalCodes({ state, commit }) {
      try {
        const response = await amplifyProxy.get(`/${state.user.ClientName}/company/nominalcodes`, {
          headers: state.config.headers
        })

        if (response.data?.Data) {
          commit('SET_NOMINAL_CODES', response.data.Data)
        }
      } catch (error) {
        throw error
      }
    },

    // Generic API helper
    async axiosGet({ state }, options) {
      try {
        return await amplifyProxy.get(`/${state.user.ClientName}/${options.url}`, {
          headers: state.config.headers,
          queryStringParameters: {
            ...options.params
          }
        })
      } catch (error) {
        throw error
      }
    }
  },
  getters: {
    // [Previous getters remain unchanged]
    loggedIn(state) {
      return !!state.user
    },
    authToken(state) {
      return state.user?.LoginToken || state.config.headers?.token || null
    },
    short_name(state) {
      return state.user?.ClientShortName || null
    },
    client_name(state) {
      return state.user?.ClientName || null
    },
    returnUserFirstName(state) {
      let activeUser = state.activeUser
      var firstName = activeUser.replace(/ .*/,'')
      return firstName
    },
    landlordsOnHold(state) {
      const landlordsToPay = state.OutgoingPayments.landlordsToPay
      let onHold = landlordsToPay.filter(landlords => landlords.OnHold == true)
      return onHold.length
    },
    tenantsOnHold(state) {
      const tenantsToPay = state.OutgoingPayments.tenantsToPay
      let onHold = tenantsToPay.filter(tenant => tenant.OnHold == true)
      return onHold.length
    },
    contractorsOnHold(state) {
      const contractorsToPay = state.OutgoingPayments.contractorsToPay
      let onHold = contractorsToPay.filter(contractor => contractor.OnHold == true)
      return onHold.length
    },
    getPropertyCountAverage(state) {
      const array = state.DashboardCards.portfolioGrowth
      return array.map(el => el.PropertyCountPerformance).reduce((acc, val) => acc + val) / array.length
    },
    getRentRollPerformanceAverage(state) {
      const array = state.DashboardCards.portfolioGrowth
      return array.map(el => el.RentRollPerformance).reduce((acc, val) => acc + val) / array.length
    },
    inArrearsUnder14(state) {
      const under14 = state.TenantsRecords.tenantsArrearsRecords.Data
      return under14.filter(tenant => tenant.TenantInDebtDuration < 14)
    },
    inArrears14To31(state) {
      const array = state.TenantsRecords.tenantsArrearsRecords.Data
      return array.filter(tenant => tenant.TenantInDebtDuration > 14 && tenant.TenantInDebtDuration <= 31)
    },
    inArrears32To62(state) {
      const array = state.TenantsRecords.tenantsArrearsRecords.Data
      return array.filter(tenant => tenant.TenantInDebtDuration > 31 && tenant.TenantInDebtDuration <= 62)
    },
    inArrearsOver62(state) {
      const array = state.TenantsRecords.tenantsArrearsRecords.Data
      return array.filter(tenant => tenant.TenantInDebtDuration > 62)
    }
  }
})